/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from 'react';
import { Accordion } from 'react-bootstrap';
import { Accessory } from '../../../../powershadesApiTypes';
import AccessoryCard from './AccessoryCard';
import SearchAndFilter from '../../../../Parts/SearchAndFilter';
import { useAssembliesByQuoteId } from '../../../../Store/entities/assemblies/hooks';

type AccessoriesAccordionProps = {
	accessories: Record<string, Accessory[]>;
	selectedAccessories: { accessory: Accessory, quantity: number }[];
	setSelectedAccessories: React.Dispatch<React.SetStateAction<{ accessory: Accessory, quantity: number }[]>>;
	quoteId: number;
};

const AccessoriesAccordion: React.FC<AccessoriesAccordionProps> = ({
	accessories,
	selectedAccessories,
	setSelectedAccessories,
	quoteId
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
	const [currentEventKeys, setCurrentEventKeys] = useState<string[]>([]);

	const allCategories = useMemo(() => Object.keys(accessories), [accessories]);
	const quoteAssemblies = useAssembliesByQuoteId(quoteId);
	const hasOutdoorShades = (quoteAssemblies?.filter((a) =>
		a.shade_type_id === 2 || a.shade_type_id === 3) || []).length > 0;

	const filteredAccessories = useMemo(() => {
		const filtered: Record<string, Accessory[]> = {};
		Object.entries(accessories).forEach(([category, accList]) => {
			if (
				(selectedCategories.length === 0 || selectedCategories.includes(category))
				&& accList && accList.some((acc) => acc.name.toLowerCase().includes(searchTerm.toLowerCase()))
			) {
				filtered[category] = accList && accList.filter((acc) =>
					acc.name.toLowerCase().includes(searchTerm.toLowerCase()));
			}
		});
		return filtered;
	}, [accessories.length, searchTerm, selectedCategories]);

	useEffect(() => {
		const matchingCategories = Object.entries(filteredAccessories)
			.filter(([_, accList]) => accList && accList.length > 0)
			.map(([category]) => category);

		setCurrentEventKeys(searchTerm ? matchingCategories : []);
	}, [searchTerm, filteredAccessories]);

	const toggleAccordion = (eventKey: string) => {
		setCurrentEventKeys((prev) =>
			(prev.includes(eventKey) ? prev.filter((key) => key !== eventKey) : [...prev, eventKey]));
	};

	return (
		<>
			<SearchAndFilter
				categories={allCategories}
				onSearchChange={setSearchTerm}
				onCategoryChange={setSelectedCategories}
			/>
			<Accordion activeKey={currentEventKeys}>
				{Object.entries(filteredAccessories).map(([category, accList]) => {
					const selectedQuantity = selectedAccessories.reduce(
						(sum, sel) => (accList && accList.includes(sel.accessory) ? sum + sel.quantity : sum),
						0
					);

					return (
						<Accordion.Item
							eventKey={category}
							key={category}
							className={`p-0 m-0 ${selectedQuantity ? 'border border-2 border-primary' : ''}`}
						>
							<Accordion.Header
								className="options-accordion m-0"
								onClick={() => toggleAccordion(category)}
							>
								<span className={selectedQuantity ? 'fas pe-3' : ''} data-count={selectedQuantity}>
									{category || 'Uncategorized'}
								</span>
							</Accordion.Header>
							<Accordion.Body>
								<div className="row row-cols-1 row-cols-md-3 g-2">
									{accList && accList.map((acc) => (
										<AccessoryCard
											key={acc.id}
											accessory={acc}
											selectedAccessories={selectedAccessories}
											setSelectedAccessories={setSelectedAccessories}
											searchTerm={searchTerm}
											hidden={!hasOutdoorShades && acc.part_number === "PS_AC_WHIP_2023_BK"}
										/>
									
									))}
								</div>
							</Accordion.Body>
						</Accordion.Item>
					);
				})}
			</Accordion>
		</>
	);
};

export default AccessoriesAccordion;
